import { AvailabilityStatus } from '@wix/ambassador-restaurants-menu-settings-v1-menu-ordering-settings/types';
import type { TimeSlot } from 'root/types/businessTypes';
import { DayOfWeek as Day } from '@wix/restaurants-olo-operations-client-commons';
import type { TFunction } from '@wix/yoshi-flow-editor';
import { extractScheduledAvailabilityData, getMenuAvailabilityStatusText } from './menusUtils';
import type { CommonAvailability } from 'root/types/availability';
import { DateTime } from 'luxon';
import type { WeeklyAvailability } from '@wix/restaurants-olo-operations-client-commons';
import type { AvailabilityStatusKeysConfig } from 'root/availabilityStatusKeys';

export const calculateAvailabilityForTimeslot = (
  availability: CommonAvailability | undefined,
  selectedTimeSlot: TimeSlot | undefined,
  timezone: string = 'America/New_York',
  businessDaysAhead?: number
) => {
  if (!availability || !selectedTimeSlot) {
    return {
      isAvailableForSlot: true,
      hasNextAvailability: false,
    };
  }

  const availabilityData = extractScheduledAvailabilityData(availability, timezone);
  const slotDateTime = selectedTimeSlot.startTime;

  const availabilityForTimeslot = { isAvailableForSlot: false, hasNextAvailability: false };

  switch (availabilityData.type) {
    case 'always_available':
    case 'none':
      availabilityForTimeslot.isAvailableForSlot = true;
      availabilityForTimeslot.hasNextAvailability = false;
      break;
    case 'timestamp_ranges': {
      const { startTime, endTime } = availabilityData.timestampRange;
      const slotTime = slotDateTime.valueOf();

      availabilityForTimeslot.isAvailableForSlot =
        slotTime >= startTime.valueOf() && slotTime <= endTime.valueOf();

      const now = DateTime.now().setZone(timezone).startOf('day');
      const availabilityStartDate = DateTime.fromJSDate(startTime).setZone(timezone).startOf('day');

      const maxBusinessDays = businessDaysAhead;
      availabilityForTimeslot.hasNextAvailability =
        slotTime < startTime.valueOf() &&
        availabilityStartDate > now &&
        availabilityStartDate <= slotDateTime.plus({ days: maxBusinessDays });
      break;
    }
    case 'weekly_schedule': {
      const { weeklyAvailability } = availabilityData;
      const slotInTimezone = slotDateTime.setZone(timezone);
      const dayOfWeek = slotInTimezone.weekday;
      const timeOfDay = slotInTimezone.hour * 60 + slotInTimezone.minute;

      const scheduleDay = getDayOfWeekFromLuxon(dayOfWeek);
      const daySchedule = weeklyAvailability[scheduleDay];

      if (daySchedule?.availabilities?.length) {
        availabilityForTimeslot.isAvailableForSlot = daySchedule.availabilities.some(
          (dailyAvailability) => {
            const openMinutes =
              (dailyAvailability.start?.hours || 0) * 60 + (dailyAvailability.start?.minutes || 0);
            const closeMinutes =
              (dailyAvailability.end?.hours || 0) * 60 + (dailyAvailability.end?.minutes || 0);
            return timeOfDay >= openMinutes && timeOfDay <= closeMinutes;
          }
        );
      } else {
        availabilityForTimeslot.isAvailableForSlot = false;
      }
      const now = DateTime.now().setZone(timezone).startOf('day');
      const maxBusinessDays = businessDaysAhead;
      const maxDate = now.plus({ days: maxBusinessDays });

      if (availabilityForTimeslot.isAvailableForSlot) {
        availabilityForTimeslot.hasNextAvailability = false;
      } else {
        availabilityForTimeslot.hasNextAvailability = checkWeeklyAvailabilityInBusinessDaysRange(
          weeklyAvailability,
          now,
          maxDate
        );
      }
      break;
    }
  }

  return availabilityForTimeslot;
};

const getDayOfWeekFromLuxon = (dayOfWeek: number): Day => {
  const dayMap = {
    1: Day.MONDAY,
    2: Day.TUESDAY,
    3: Day.WEDNESDAY,
    4: Day.THURSDAY,
    5: Day.FRIDAY,
    6: Day.SATURDAY,
    7: Day.SUNDAY,
  };
  return dayMap[dayOfWeek as keyof typeof dayMap];
};

const checkWeeklyAvailabilityInBusinessDaysRange = (
  weeklyAvailability: WeeklyAvailability,
  startDate: DateTime,
  endDate: DateTime
): boolean => {
  let currentDate = startDate.plus({ days: 1 });
  while (currentDate <= endDate) {
    const dayOfWeek = currentDate.weekday;
    const scheduleDay = getDayOfWeekFromLuxon(dayOfWeek);
    const daySchedule = weeklyAvailability[scheduleDay];
    if (daySchedule?.availabilities?.length) {
      return true;
    }

    currentDate = currentDate.plus({ days: 1 });
  }

  return false;
};

export const getAvailabilityStatusText = (
  state: {
    availability?: CommonAvailability;
    hasNextAvailableTimeslot?: boolean;
  },
  locale: string,
  timezone: string,
  t: TFunction,
  keys: AvailabilityStatusKeysConfig
) => {
  const hasNextAvailability = !!state.hasNextAvailableTimeslot;

  if (!state.availability) {
    return { shouldCollapseAvailabilityStatus: true, text: '' };
  }

  return getMenuAvailabilityStatusText({
    availability: state.availability,
    locale,
    timezone,
    t,
    keys: hasNextAvailability ? keys.hasNextAvailabilityKeys : keys.noNextAvailabilityKeys,
  });
};

export const initializeAvailability = (
  availability: CommonAvailability | undefined,
  selectedTimeSlot: TimeSlot | undefined,
  timezone: string | undefined,
  updateAvailabilityStatus: (status: AvailabilityStatus) => void,
  updateNextAvailableTimeslot: (hasNext: boolean) => void,
  businessDaysAhead?: number
) => {
  if (availability) {
    const result = calculateAvailabilityForTimeslot(
      availability,
      selectedTimeSlot,
      timezone,
      businessDaysAhead
    );
    updateAvailabilityStatus(
      result.isAvailableForSlot ? AvailabilityStatus.AVAILABLE : AvailabilityStatus.UNAVAILABLE
    );
    updateNextAvailableTimeslot(result.hasNextAvailability);
  } else {
    updateAvailabilityStatus(AvailabilityStatus.AVAILABLE);
    updateNextAvailableTimeslot(false);
  }
};
